import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteScopedCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteScopedCompaniesTypeahead';
import TextInput from '~components/FormFields/v2/TextInput';
import { MaterialType } from '~hooks/useMaterials/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  MaterialTypeDto,
  MaterialTypeDtoAsyncError,
  materialTypeSchema,
  setMaterialTypeSchemaDefaultValues,
} from './materialTypeSchema';

export type MaterialTypeFormHandler = {
  onSubmit: () => Promise<MaterialTypeDto | undefined>;
};

interface MaterialTypeFormProps {
  asyncErrors?: MaterialTypeDtoAsyncError[];
  materialType?: MaterialType;
}

const MaterialTypeForm = forwardRef<MaterialTypeFormHandler, MaterialTypeFormProps>(
  function MaterialTypeForm({ asyncErrors, materialType }, ref) {
    const { userStore } = useStores();

    const isEditing = Boolean(materialType);

    const form = useForm<MaterialTypeDto>({
      resolver: yupResolver(materialTypeSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setMaterialTypeSchemaDefaultValues(userStore, materialType),
    });

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            form.handleSubmit(
              (data) => resolve(data),
              // resolve undefined on validation failure so the awaiting caller doesn't hang
              () => resolve(undefined),
            )();
          });
        },
      }),
      [form.handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          form.setError(error.field as keyof MaterialTypeDto, {
            message: error.message,
          });
        }
      }

      return () => {
        form.clearErrors();
      };
    }, [asyncErrors]);

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        <Box sx={{ gridColumn: 'span 2' }}>
          <AutocompleteScopedCompaniesTypeahead
            companyId={getEffectiveUserCompanyId(userStore)}
            control={form.control}
            disableClearable
            isDisabled={isEditing}
            isRequired
            label={t('form_fields.company')}
            name="company"
          />
        </Box>

        <Box>
          <TextInput
            control={form.control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />
        </Box>

        <Box>
          <TextInput
            control={form.control}
            label={t('form_fields.external_id')}
            name="externalId"
          />
        </Box>
      </Box>
    );
  },
);

export default MaterialTypeForm;
